// @flow
import { v4 as uuidv4 } from 'uuid';
import { apiService } from '../../services/APIService';
import type {
  FetchAllWorkoutsAction,
  FetchAllWorkoutsFailureAction,
  FetchAllWorkoutsSuccessAction,
  FetchRecommendedWorkoutsAction,
  FetchRecommendedWorkoutsFailureAction,
  FetchRecommendedWorkoutsSuccessAction,
  FilterWorkoutsAction,
  FilterWorkoutsFailureAction,
  FilterWorkoutsResetAction,
  FilterWorkoutsSuccessAction,
  MarkWorkoutDoneAction,
  MarkWorkoutDoneFailedAction,
  MarkWorkoutDoneSuccessAction,
  MarkWorkoutStartedAction,
  MarkWorkoutStartedFailedAction,
  MarkWorkoutStartedSuccessAction,
} from '../../types/Action';
import type { Dispatch } from '../../types/Dispatch';
import {
  FETCH_RECOMMENDED_WORKOUTS_ACTION,
  FETCH_RECOMMENDED_WORKOUTS_FAILED,
  FETCH_RECOMMENDED_WORKOUTS_SUCCESS,
  MARK_WORKOUT_STARTED,
  MARK_WORKOUT_STARTED_SUCCESS,
  MARK_WORKOUT_STARTED_FAILED,
  MARK_WORKOUT_DONE_SUCCESS,
  FETCH_ALL_WORKOUTS_STARTED,
  FETCH_ALL_WORKOUTS_SUCCESS,
  FETCH_ALL_WORKOUTS_FAILED,
  FILTER_ALL_WORKOUTS_RESET,
  FILTER_ALL_WORKOUTS_STARTED,
  FILTER_ALL_WORKOUTS_SUCCESS, FILTER_ALL_WORKOUTS_FAILED,
  SET_NEXT_WORKOUT
} from './types';
import type { ApiResponseWorkout } from '../../types/ApiResponseWorkout';
import type { User } from '../../types/User';
import AutopilotAPI from '../../services/AutopilotService';
import type { Tag } from '../../types/Tag';
import Workout from '../../modules/Workout';
import { adjustTrackingProvider } from '../../services/AdjustTrackingProvider';
import {
  FIRST_TRAINING_COMPLETED_EVENT,
  PROGRAM_COMPLETED_EVENT,
  WORKOUT_DONE_EVENT,
} from '../../config/trackingEventNames';
import { logger } from '../../modules/Logger';
import getWorkoutFromPlanWorkoutsById from '../../modules/getWorkoutFromPlanWorkoutsById';

type AllowedFetchAllWorkoutsDispatchTypes =
  | FetchAllWorkoutsAction
  | FetchAllWorkoutsSuccessAction
  | FetchAllWorkoutsFailureAction

type AllowedFetchRecommendedWorkoutsDispatchTypes =
  | FetchRecommendedWorkoutsAction
  | FetchRecommendedWorkoutsSuccessAction
  | FetchRecommendedWorkoutsFailureAction

type AllowedMarkWorkoutStartedTypes =
  | MarkWorkoutStartedAction
  | MarkWorkoutStartedSuccessAction
  | MarkWorkoutStartedFailedAction

type AllowedMarkWorkoutDoneTypes =
  | MarkWorkoutDoneAction
  | MarkWorkoutDoneSuccessAction
  | MarkWorkoutDoneFailedAction

type AllowedFilterWorkoutTypes =
  | FilterWorkoutsAction
  | FilterWorkoutsSuccessAction
  | FilterWorkoutsFailureAction

type MarkWorkoutStartedActionPayload = {|
  programId?: number,
  workoutId: number,
|};

export type MarkWorkoutDoneActionPayload = {|
  sessionId: string,
    workoutId: number,
|};

// eslint-disable-next-line import/prefer-default-export
export const workoutsActionCreators: {|
  filterWorkouts: (tag: Tag) => (Dispatch<AllowedFilterWorkoutTypes>) => Promise<void>,
  resetFilteredWorkouts: () => FilterWorkoutsResetAction,
  fetchAllWorkouts: () => (Dispatch<AllowedFetchAllWorkoutsDispatchTypes>) => Promise<void>,
  fetchRecommendedWorkouts: () => (Dispatch<AllowedFetchRecommendedWorkoutsDispatchTypes>) => Promise<void>,
  markWorkoutStarted: (MarkWorkoutStartedActionPayload) => (Dispatch<AllowedMarkWorkoutStartedTypes>) => Promise<void>,
  markWorkoutDone: (payload: MarkWorkoutDoneActionPayload) => (Dispatch<AllowedMarkWorkoutDoneTypes>, Function) => Promise<void>,
  setNextWorkout: (payload: MarkWorkoutDoneActionPayload) => (Dispatch<AllowedMarkWorkoutDoneTypes>, Function) => void,
|} = {
  fetchAllWorkouts: () => async (dispatch) => {
    dispatch({ type: FETCH_ALL_WORKOUTS_STARTED });

    try {
      const apiResponseWorkouts: ApiResponseWorkout[] = await apiService.fetchWorkouts();

      dispatch({
        type: FETCH_ALL_WORKOUTS_SUCCESS,
        payload: apiResponseWorkouts,
      });
    } catch (error) {
      dispatch({
        type: FETCH_ALL_WORKOUTS_FAILED,
        payload: { error },
      });
      logger.error(error, [
        {
          fileName: 'workout.js',
          className: 'workoutsActionCreators',
          functionName: 'fetchAllWorkouts()',
        },
      ]);
    }
  },

  resetFilteredWorkouts: () => ({ type: FILTER_ALL_WORKOUTS_RESET }),

  filterWorkouts: (tag: Tag) => async (dispatch) => {
    dispatch({
      type: FILTER_ALL_WORKOUTS_STARTED,
    });

    try {
      const filteredWorkouts: Workout[] = await apiService.getWorkoutsByTag([tag]);

      dispatch({
        type: FILTER_ALL_WORKOUTS_SUCCESS,
        payload: filteredWorkouts,
      });
    } catch (error) {
      dispatch({
        type: FILTER_ALL_WORKOUTS_FAILED,
        payload: { error },
      });
      logger.error(error, [
        {
          fileName: 'workout.js',
          className: 'workoutsActionCreators',
          functionName: 'filterWorkouts()',
        },
      ]);
    }
  },

  fetchRecommendedWorkouts: () => async (dispatch) => {
    dispatch({
      type: FETCH_RECOMMENDED_WORKOUTS_ACTION,
    });

    try {
      const apiResponseWorkouts: ApiResponseWorkout[] = await apiService.fetchRecommendedWorkouts();

      dispatch({
        type: FETCH_RECOMMENDED_WORKOUTS_SUCCESS,
        payload: apiResponseWorkouts,
      });
    } catch (error) {
      dispatch({
        type: FETCH_RECOMMENDED_WORKOUTS_FAILED,
        payload: { error },
      });
      logger.error(error, [
        {
          fileName: 'workout.js',
          className: 'workoutsActionCreators',
          functionName: 'fetchRecommendedWorkouts()',
        },
      ]);
    }
  },

  markWorkoutStartedBySlug: ({
    userId,
    slug,
    planSlug,
    workoutId
  }) => async (dispatch) => {
    console.log({ userId, slug, planSlug });

    dispatch({
      type: MARK_WORKOUT_STARTED,
      payload: {
        workoutId,
      },
    });

    try {
      const { sessionId } = await apiService.markWorkoutStartedBySlug({
        userId: userId,
        slug: slug,
        planSlug: planSlug,
        sessionId: uuidv4(),
        startedAt: new Date().toISOString(),
      });

      dispatch({
        type: MARK_WORKOUT_STARTED_SUCCESS,
        payload: {
          sessionId,
        },
      });
    } catch (error) {
      dispatch({
        type: MARK_WORKOUT_STARTED_FAILED,
        payload: {
          error,
          workoutId,
        },
      });
      logger.error(error, [
        {
          fileName: 'workout.js',
          className: 'workoutsActionCreators',
          functionName: 'markWorkoutStarted()',
        },
      ]);
    }
  },

  markWorkoutStarted: (payload: MarkWorkoutStartedActionPayload) => async (dispatch) => {
    const {
      workoutId,
      programId,
    } = payload;

    dispatch({
      type: MARK_WORKOUT_STARTED,
      payload: {
        workoutId,
      },
    });

    try {
      const { sessionId } = await apiService.markWorkoutStarted({
        workoutId,
        programId,
        sessionId: uuidv4(),
        startedAt: new Date().toISOString(),
      });

      dispatch({
        type: MARK_WORKOUT_STARTED_SUCCESS,
        payload: {
          sessionId,
        },
      });
    } catch (error) {
      dispatch({
        type: MARK_WORKOUT_STARTED_FAILED,
        payload: {
          error,
          workoutId,
        },
      });
      logger.error(error, [
        {
          fileName: 'workout.js',
          className: 'workoutsActionCreators',
          functionName: 'markWorkoutStarted()',
        },
      ]);
    }
  },

  setNextWorkout: ({ workoutId }) => (dispatch, getState) => {

    try {
      dispatch({ type: SET_NEXT_WORKOUT, payload: { workoutId } });
    } catch(e) {

    }
  },

  markWorkoutDone: ({ sessionId, workoutId }: MarkWorkoutDoneActionPayload) => async (dispatch, getState) => {
    const { activePlan } = getState();

    try {
      const { totalFinishedWorkouts } = await apiService.markWorkoutDone({
        sessionId,
        completedAt: new Date().toISOString(),
      });

      dispatch({ type: MARK_WORKOUT_DONE_SUCCESS, payload: { workoutId } });

      const activePlanWorkoutIds = (activePlan && activePlan.workouts.map((w) => w.id)) || [];
      const planId = activePlanWorkoutIds.includes(workoutId) ? activePlan.id : null;

      adjustTrackingProvider.trackEvent(WORKOUT_DONE_EVENT, {
        workoutId,
        planId,
      });

      if (totalFinishedWorkouts === 1) {
        adjustTrackingProvider.trackEvent(FIRST_TRAINING_COMPLETED_EVENT, {
          workoutId,
          planId,
        });
      }

      const user: User = (getState().auth.user: any);
      const planWorkout = getWorkoutFromPlanWorkoutsById({ workoutId, planWorkouts: activePlan.workouts });

      AutopilotAPI.setTotalVideosWatched(user, totalFinishedWorkouts);
      AutopilotAPI.lastVideoWatched(user, planWorkout && planWorkout.name);

      // If workout id is part of the active program
      // And all the workouts have been completed
      // emit the program completed event
      if (activePlanWorkoutIds.includes(workoutId)) {
        // get fresh active plan
        const isProgramDone = getState().activePlan.isDone;

        if (isProgramDone === true) {
          adjustTrackingProvider.trackEvent(PROGRAM_COMPLETED_EVENT, {
            planId,
          });
        }
      }
    } catch (e) {
      logger.error(e, [
        {
          fileName: 'workout.js',
          className: 'workoutsActionCreators',
          functionName: 'markWorkoutDone()',
        },
      ]);
    }
  },

  markWorkoutDoneBySlug: ({ sessionId, workoutId, activePlan }: MarkWorkoutDoneActionPayload) => async (dispatch, getState) => {
    
    console.log({ activePlan123: activePlan });

    try {
      const { totalFinishedWorkouts } = await apiService.markWorkoutDone({
        sessionId,
        completedAt: new Date().toISOString(),
      });

      dispatch({ type: MARK_WORKOUT_DONE_SUCCESS, payload: { workoutId } });

      const activePlanWorkoutIds = (activePlan && activePlan.workouts.map((w) => w.id)) || [];
      const planId = activePlanWorkoutIds.includes(workoutId) ? activePlan.id : null;

      adjustTrackingProvider.trackEvent(WORKOUT_DONE_EVENT, {
        workoutId,
        planId,
      });

      if (totalFinishedWorkouts === 1) {
        adjustTrackingProvider.trackEvent(FIRST_TRAINING_COMPLETED_EVENT, {
          workoutId,
          planId,
        });
      }

      const user: User = (getState().auth.user: any);
      const planWorkout = getWorkoutFromPlanWorkoutsById({ workoutId, planWorkouts: activePlan.workouts });

      AutopilotAPI.setTotalVideosWatched(user, totalFinishedWorkouts);
      AutopilotAPI.lastVideoWatched(user, planWorkout && planWorkout.name);

      // If workout id is part of the active program
      // And all the workouts have been completed
      // emit the program completed event
      if (activePlanWorkoutIds.includes(workoutId)) {
        // get fresh active plan
        const isProgramDone = getState().activePlan.isDone;

        if (isProgramDone === true) {
          adjustTrackingProvider.trackEvent(PROGRAM_COMPLETED_EVENT, {
            planId,
          });
        }
      }
    } catch (e) {
      logger.error(e, [
        {
          fileName: 'workout.js',
          className: 'workoutsActionCreators',
          functionName: 'markWorkoutDone()',
        },
      ]);
    }
  },
};
